import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import { SubgridEmptyState } from '@/components/SubgridEmptyState';
import {
  deleteDefinition,
  getDefinitions,
  type DefinitionKind,
} from '@/features/settings/api/definitions';
import { StandardTasksPanel } from '@/features/settings/components/StandardTasksPanel';

export function SettingsDefinitionsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [kind, setKind] = useState<DefinitionKind | 'tasks'>(
    params.get('tab') === 'tasks' ? 'tasks' : 'readings',
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['settings', kind === 'tasks' ? 'readings' : kind],
    queryFn: () => getDefinitions(kind === 'dosages' ? 'dosages' : 'readings'),
    enabled: kind !== 'tasks',
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['settings', kind] });
  const remove = useMutation({
    mutationFn: (id: number) => deleteDefinition(kind === 'dosages' ? 'dosages' : 'readings', id),
    onSuccess: async () => {
      setSelectedId(null);
      await refresh();
    },
  });
  const rows = query.data ?? [];
  const selected = rows.find((item) => item.id === selectedId) ?? null;
  const singular = kind === 'readings' ? 'Reading' : 'Dosage';

  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 48, px: 1 }}>
          <IconButton size="small" aria-label="Back" onClick={() => history.back()}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          <Button startIcon={<RefreshIcon />} onClick={() => void refresh()} sx={commandButtonSx}>
            Refresh
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button component={RouterLink} to="/settings/technician-dashboard" sx={commandButtonSx}>
            Technician dashboard
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5 }}>
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Typography variant="h1">Settings</Typography>
          <Typography color="text.secondary">
            Standard technician tasks and water treatment definitions.
          </Typography>
        </Box>
        <Tabs
          value={kind}
          onChange={(_, value: DefinitionKind | 'tasks') => {
            setKind(value);
            setSelectedId(null);
          }}
          sx={{ px: 2.5, mt: 1 }}
        >
          <Tab value="readings" label="Readings" />
          <Tab value="dosages" label="Dosages" />
          <Tab value="tasks" label="Tasks" />
        </Tabs>
      </Paper>
      {kind === 'tasks' ? (
        <Box sx={{ mt: 1.5 }}>
          <StandardTasksPanel />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 1.5, overflowX: 'auto' }}>
          <Stack direction="row" justifyContent="flex-end" sx={subgridCommandSx}>
            {selected ? (
              <>
                <Button
                  component={RouterLink}
                  to={`/settings/${kind}/${selected.id}`}
                  startIcon={<EditOutlinedIcon />}
                  sx={commandButtonSx}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<DeleteOutlineIcon />}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(selected.id)}
                  sx={deleteCommandButtonSx}
                >
                  Delete
                </Button>
              </>
            ) : (
              <Button
                component={RouterLink}
                to={`/settings/${kind}/new`}
                startIcon={<AddIcon />}
                sx={commandButtonSx}
              >
                New {singular}
              </Button>
            )}
          </Stack>
          {query.isError && <Alert severity="error">These settings could not be loaded.</Alert>}
          {remove.isError && (
            <Alert severity="error">The selected definition could not be deleted.</Alert>
          )}
          <Box sx={{ minWidth: 700 }}>
            <Box sx={gridRowSx}>
              <Box />
              <Typography fontWeight={600}>Name</Typography>
              <Typography fontWeight={600}>Unit</Typography>
              <Typography fontWeight={600}>Minimum</Typography>
              <Typography fontWeight={600}>Maximum</Typography>
              <Typography fontWeight={600}>Step</Typography>
            </Box>
            {rows.map((item) => {
              const active = selectedId === item.id;
              const toggle = () => setSelectedId(active ? null : item.id);
              return (
                <Box
                  key={item.id}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open ${item.name}`}
                  onClick={() => void navigate(`/settings/${kind}/${item.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void navigate(`/settings/${kind}/${item.id}`);
                    }
                  }}
                  sx={{
                    ...gridRowSx,
                    cursor: 'pointer',
                    bgcolor: active ? '#e5f1fb' : 'transparent',
                    '&:hover': { bgcolor: active ? '#d7eaf8' : '#f3f2f1' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={active}
                    inputProps={{ 'aria-label': `Select ${item.name}` }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={toggle}
                  />
                  <Typography>{item.name}</Typography>
                  <Typography>{item.unit}</Typography>
                  <Typography>{item.min.toFixed(2)}</Typography>
                  <Typography>{item.max.toFixed(2)}</Typography>
                  <Typography>{item.step.toFixed(2)}</Typography>
                </Box>
              );
            })}
          </Box>
          {!query.isLoading && !rows.length && !query.isError && (
            <SubgridEmptyState message="We didn't find anything to show here" />
          )}
          <Typography variant="body2" sx={{ pt: 1.5 }}>
            Rows: {rows.length}
            {selected ? ' · Selected: 1' : ''}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const deleteCommandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'error.main' },
};
const subgridCommandSx = { minHeight: 44, borderBottom: '1px solid #e1dfdd' };
const gridRowSx = {
  display: 'grid',
  gridTemplateColumns: '48px minmax(180px,1fr) 130px 100px 100px 100px',
  alignItems: 'center',
  minHeight: 48,
  px: 1,
  borderBottom: '1px solid #edebe9',
  gap: 1,
};
